import { Button, Checkbox, Input, Stack, Text } from '@sixfold/common-ui';
import React, { useState } from 'react';

export interface OvdTokenFormValues {
  label: string;
  platformIds: string;
  shipperIds: string;
  carrierIds: string;
  deliveryCustomFields: string;
  tourCustomFields: string;
  enableStatus: boolean;
  enableApiEta: boolean;
  enableCustomFields: boolean;
  enableCancellationReason: boolean;
  enableLocation: boolean;
}

interface Props {
  initialValues?: Partial<OvdTokenFormValues>;
  onSubmit: (values: OvdTokenFormValues) => void;
  onCancel: () => void;
  isSubmitting: boolean;
  submitLabel: string;
  extraActions?: React.ReactNode | ((values: OvdTokenFormValues) => React.ReactNode);
}

const defaultValues: OvdTokenFormValues = {
  label: '',
  platformIds: '',
  shipperIds: '',
  carrierIds: '',
  deliveryCustomFields: '',
  tourCustomFields: '',
  enableStatus: false,
  enableApiEta: false,
  enableCustomFields: false,
  enableCancellationReason: false,
  enableLocation: false,
};

export const OvdTokenForm: React.FC<Props> = ({
  initialValues,
  onSubmit,
  onCancel,
  isSubmitting,
  submitLabel,
  extraActions,
}) => {
  const [values, setValues] = useState<OvdTokenFormValues>({
    ...defaultValues,
    ...initialValues,
  });
  const [validationErrors, setValidationErrors] = useState<Partial<Record<keyof OvdTokenFormValues, string>>>({});

  const validate = (vals: OvdTokenFormValues): Partial<Record<keyof OvdTokenFormValues, string>> => {
    const errors: Partial<Record<keyof OvdTokenFormValues, string>> = {};
    if (vals.label.trim().length === 0) {
      errors.label = 'Label is required';
    }
    if (parseCommaSeparated(vals.platformIds).length === 0) {
      errors.platformIds = 'At least one platform ID is required';
    }
    return errors;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const errors = validate(values);
    setValidationErrors(errors);
    if (Object.keys(errors).length > 0) {
      return;
    }
    onSubmit(values);
  };

  return (
    <form onSubmit={handleSubmit}>
      <Stack direction="column" gap="medium">
        <Input
          label="Label"
          name="label"
          value={values.label}
          onChange={({ value }) => {
            setValues((v) => ({ ...v, label: value }));
            if (validationErrors.label && value.trim().length > 0) {
              setValidationErrors((prev) => ({ ...prev, label: undefined }));
            }
          }}
          errorMessage={validationErrors.label}
          required
        />

        <Input
          label="Platform IDs (comma-separated)"
          name="platformIds"
          value={values.platformIds}
          onChange={({ value }) => {
            setValues((v) => ({ ...v, platformIds: value }));
            if (validationErrors.platformIds && parseCommaSeparated(value).length > 0) {
              setValidationErrors((prev) => ({ ...prev, platformIds: undefined }));
            }
          }}
          errorMessage={validationErrors.platformIds}
          required
        />

        <Input
          label="Shipper IDs (comma-separated, optional)"
          name="shipperIds"
          value={values.shipperIds}
          onChange={({ value }) => setValues((v) => ({ ...v, shipperIds: value }))}
        />

        <Input
          label="Carrier IDs (comma-separated, optional)"
          name="carrierIds"
          value={values.carrierIds}
          onChange={({ value }) => setValues((v) => ({ ...v, carrierIds: value }))}
        />

        <Input
          label="Delivery Custom Fields (comma-separated key:value, optional)"
          name="deliveryCustomFields"
          value={values.deliveryCustomFields}
          onChange={({ value }) => setValues((v) => ({ ...v, deliveryCustomFields: value }))}
        />

        <Input
          label="Tour Custom Fields (comma-separated key:value, optional)"
          name="tourCustomFields"
          value={values.tourCustomFields}
          onChange={({ value }) => setValues((v) => ({ ...v, tourCustomFields: value }))}
        />

        <Text weight="bold">Feature Flags</Text>
        <Stack direction="column" gap="small">
          <Checkbox
            label="Enable Status"
            checked={values.enableStatus}
            onChange={() => setValues((v) => ({ ...v, enableStatus: !v.enableStatus }))}
          />
          <Checkbox
            label="Enable API ETA"
            checked={values.enableApiEta}
            onChange={() => setValues((v) => ({ ...v, enableApiEta: !v.enableApiEta }))}
          />
          <Checkbox
            label="Enable Custom Fields"
            checked={values.enableCustomFields}
            onChange={() => setValues((v) => ({ ...v, enableCustomFields: !v.enableCustomFields }))}
          />
          <Checkbox
            label="Enable Cancellation Reason"
            checked={values.enableCancellationReason}
            onChange={() => setValues((v) => ({ ...v, enableCancellationReason: !v.enableCancellationReason }))}
          />
          <Checkbox
            label="Enable Location"
            checked={values.enableLocation}
            onChange={() => setValues((v) => ({ ...v, enableLocation: !v.enableLocation }))}
          />
        </Stack>

        <Stack direction="row" gap="small" alignItems="center">
          <Button type="submit" kind="primary" disabled={isSubmitting}>
            {submitLabel}
          </Button>
          <Button type="button" kind="subtle" onClick={onCancel} disabled={isSubmitting}>
            Cancel
          </Button>
          {typeof extraActions === 'function' ? extraActions(values) : extraActions}
        </Stack>
      </Stack>
    </form>
  );
};

export function parseCommaSeparated(value: string): string[] {
  return value
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

export function toCommaSeparated(values: string[]): string {
  return values.join(', ');
}
